class ReviewsWithVideos extends HTMLElement {
	constructor() {
		super();
		this.container = this.querySelector('.reviews-with-videos__grid');
    this.loadMoreBtn = this.querySelector('.load-more-btn');
    this.loadMoreBtnSpan = this.querySelector('.load-more-btn-span'); // For text content
		this.blocks = Array.from(this.querySelectorAll('.review-card'));

		// Get settings from data attributes
		this.autoplayEnabled = this.dataset.autoplay === 'true';
		this.initialLoad = parseInt(this.dataset.initialLoad) || 8;
		this.loadIncrement = Math.max(1, Math.min(8, parseInt(this.dataset.loadIncrement) || 4)); // Ensure between 1-8

		// State management
		this.currentlyVisible = 0;
		this.isExpanded = false;

    // Get button texts from section settings
    this.loadMoreText = this.loadMoreBtnSpan?.textContent.trim() || 'View More Reviews';
    this.showLessText = document.querySelector('[data-show-less-text]')?.dataset.showLessText || 'Show Less';

		// Video modal elements
		this.videoModal = document.getElementById('videoModal');
		this.modalVideoContainer = this.videoModal?.querySelector('.video-container');
		this.modalClose = this.videoModal?.querySelector('.br-icon__close');
		this.modalPrev = this.videoModal?.querySelector('.br-icon__before');
		this.modalNext = this.videoModal?.querySelector('.br-icon__after');

		// Video navigation state
		this.currentVideoIndex = 0;
		this.videoBlocks = [];

		// Masonry layout properties
		this.masonry = {
			columns: [],
			columnHeights: [],
			columnCount: 4,
			gap: 24,
			itemWidth: 0
		};

		this.init();
	}

	init() {
		this.calculateMasonrySettings();
		this.initializeMasonryLayout();
		this.setupInitialState();
		this.setupEventListeners();
		this.setupVideoHandling();
		this.setupIntersectionObserver();
		this.setupTextTruncation();
		this.setupVideoModal();

		// Handle window resize - only recalculate settings, don't relayout automatically
		window.addEventListener('resize', this.handleResize.bind(this));
	}

	calculateMasonrySettings() {
		const containerWidth = this.container.offsetWidth;
		const screenWidth = window.innerWidth;

		// Calculate responsive column count and gap
		if (screenWidth <= 480) {
			this.masonry.columnCount = 2;
			this.masonry.gap = 16; // 16px gap for mobile
		} else if (screenWidth <= 768) {
			this.masonry.columnCount = 2;
			this.masonry.gap = 16; // 16px gap for mobile
		} else if (screenWidth <= 1200) {
			this.masonry.columnCount = 3;
			this.masonry.gap = 24; // Default gap for tablet
		} else {
			this.masonry.columnCount = 4;
			this.masonry.gap = 24; // Default gap for desktop
		}

		// Calculate item width based on columns and gaps
		const totalGaps = (this.masonry.columnCount - 1) * this.masonry.gap;
		this.masonry.itemWidth = (containerWidth - totalGaps) / this.masonry.columnCount;

		// Masonry settings calculated
	}

	initializeMasonryLayout() {
		// Set container to relative positioning for absolute positioned items
		this.container.style.position = 'relative';

		// Initialize column heights array
		this.masonry.columnHeights = new Array(this.masonry.columnCount).fill(0);

		// Position only the initially visible items (first 8)
		const initialItems = this.blocks.slice(0, this.initialLoad);

		// Sort items for mobile alternating pattern if needed
		const sortedItems = this.sortItemsForMobile(initialItems);
		this.positionItemsSequentially(sortedItems);
	}

	positionMasonryItem(item, index) {
		// Find the shortest column
		const shortestColumnIndex = this.getShortestColumnIndex();

		// Set item styles
		item.style.position = 'absolute';
		item.style.width = `${this.masonry.itemWidth}px`;
		item.style.left = `${shortestColumnIndex * (this.masonry.itemWidth + this.masonry.gap)}px`;
		item.style.top = `${this.masonry.columnHeights[shortestColumnIndex]}px`;

		// Add column number attribute (1-based indexing for readability)
		item.setAttribute('data-column-no', shortestColumnIndex + 1);

		// Force layout calculation and get actual height
		item.style.visibility = 'hidden';
		item.style.display = 'block';

		// Force layout calculation immediately for iOS optimization
		const itemHeight = item.offsetHeight || this.estimateItemHeight(item);

		// Update column height immediately
		this.masonry.columnHeights[shortestColumnIndex] += itemHeight + this.masonry.gap;

		// Show the item immediately to prevent flickering
		item.style.visibility = 'visible';

		// Update container height
		const maxHeight = Math.max(...this.masonry.columnHeights);
		this.container.style.height = `${maxHeight}px`;
	}

	estimateItemHeight(item) {
		// Estimate height based on item type and content
		if (item.classList.contains('review-card--video')) {
			return 400; // Standard video card height
		} else if (item.classList.contains('review-card--text')) {
			// Estimate text card height based on content
			const textElement = item.querySelector('.review-card__text');
			const textLength = textElement ? textElement.textContent.length : 100;
			return Math.min(150 + (textLength * 0.8), 350); // Base height + text factor
		}
		return 250; // Default fallback height
	}

	getShortestColumnIndex() {
		let shortestIndex = 0;
		let shortestHeight = this.masonry.columnHeights[0];

		for (let i = 1; i < this.masonry.columnHeights.length; i++) {
			if (this.masonry.columnHeights[i] < shortestHeight) {
				shortestHeight = this.masonry.columnHeights[i];
				shortestIndex = i;
			}
		}

		return shortestIndex;
	}

	relayoutMasonry() {
		// Reset column heights
		this.masonry.columnHeights = new Array(this.masonry.columnCount).fill(0);

		// Get all visible items
		const visibleItems = this.blocks.filter(block => !block.hasAttribute('data-hidden'));

		// Sort items for mobile alternating pattern if needed
		const sortedItems = this.sortItemsForMobile(visibleItems);

		// Reposition all visible items sequentially
		this.positionItemsSequentially(sortedItems);
	}

	sortItemsForMobile(items) {
		// Only apply mobile sorting for 2 columns (mobile)
		if (this.masonry.columnCount !== 2) {
			return items;
		}

		// Separate items by type
		const textItems = items.filter(item => item.classList.contains('review-card--text'));
		const videoItems = items.filter(item => item.classList.contains('review-card--video'));

		const sortedItems = [];

		// Create alternating pattern for 2 columns:
		// Position 0 (left): text    Position 1 (right): video
		// Position 2 (left): video   Position 3 (right): text  
		// Position 4 (left): text    Position 5 (right): video
		// Position 6 (left): video   Position 7 (right): text

		let textIndex = 0;
		let videoIndex = 0;

		for (let i = 0; i < items.length; i++) {
			const rowIndex = Math.floor(i / 2); // Which row (0, 1, 2, 3...)
			const isLeftColumn = (i % 2 === 0); // Is this the left column?

			if (rowIndex % 2 === 0) {
				// Even rows: text-video pattern
				if (isLeftColumn && textIndex < textItems.length) {
					sortedItems.push(textItems[textIndex++]);
				} else if (!isLeftColumn && videoIndex < videoItems.length) {
					sortedItems.push(videoItems[videoIndex++]);
				}
			} else {
				// Odd rows: video-text pattern
				if (isLeftColumn && videoIndex < videoItems.length) {
					sortedItems.push(videoItems[videoIndex++]);
				} else if (!isLeftColumn && textIndex < textItems.length) {
					sortedItems.push(textItems[textIndex++]);
				}
			}
		}

		// Add any remaining items that didn't fit the pattern
		while (textIndex < textItems.length) {
			sortedItems.push(textItems[textIndex++]);
		}
		while (videoIndex < videoItems.length) {
			sortedItems.push(videoItems[videoIndex++]);
		}

		return sortedItems.filter(item => item); // Remove any undefined items
	}

	positionItemsSequentially(items) {
		if (items.length === 0) return;

		// Position items with minimal delay for iOS optimization
		items.forEach((item, index) => {
			if (index === 0) {
				// Position first item immediately
				this.positionMasonryItemSync(item);
			} else {
				// Minimal delay for subsequent items to prevent iOS glitches
				setTimeout(() => {
					this.positionMasonryItemSync(item);
				}, index * 5); // Reduced delay from 20ms to 5ms
			}
		});
	}

	positionMasonryItemSync(item) {
		// Find the shortest column
		const shortestColumnIndex = this.getShortestColumnIndex();

		// Calculate position
		const leftPos = shortestColumnIndex * (this.masonry.itemWidth + this.masonry.gap);
		const topPos = this.masonry.columnHeights[shortestColumnIndex];

		// Set item styles
		item.style.position = 'absolute';
		item.style.width = `${this.masonry.itemWidth}px`;
		item.style.left = `${leftPos}px`;
		item.style.top = `${topPos}px`;

		// Add column number attribute (1-based indexing for readability)
		item.setAttribute('data-column-no', shortestColumnIndex + 1);

		// Column assignment completed

		// Show the item immediately to prevent iOS flickering
		item.style.opacity = '1';

		// Get actual height immediately (item should already be rendered)
		const itemHeight = item.offsetHeight || this.estimateItemHeight(item);

		// Update column height
		this.masonry.columnHeights[shortestColumnIndex] += itemHeight + this.masonry.gap;

		// Update container height to fit only visible content
		this.updateContainerHeight();
	}

	updateContainerHeight() {
		// Calculate height based only on visible items
		const visibleColumnHeights = [...this.masonry.columnHeights];
		const maxHeight = Math.max(...visibleColumnHeights);

		// Smooth height transitions to prevent iOS jerking
		const currentHeight = parseInt(this.container.style.height) || 0;
		const heightDifference = Math.abs(maxHeight - currentHeight);
		
		// Only update if height change is significant to prevent micro-adjustments
		if (heightDifference > 10) {
			// Use requestAnimationFrame for smoother updates on iOS
			requestAnimationFrame(() => {
				this.container.style.height = `${maxHeight}px`;
			});
		}

		// Position the load more button at the next item position
		this.positionLoadMoreButton();
	}

  positionLoadMoreButton() {
    if (!this.loadMoreBtn || !this.loadMoreBtnSpan || this.currentlyVisible >= this.blocks.length) {
      return;
    }

		// Calculate column heights based only on currently visible items
		const visibleColumnHeights = new Array(this.masonry.columnCount).fill(0);

		// Go through only the visible items and calculate their column heights
		for (let i = 0; i < this.currentlyVisible; i++) {
			const item = this.blocks[i];
			if (item && !item.hasAttribute('data-hidden')) {
				const itemLeft = parseInt(item.style.left) || 0;
				const itemTop = parseInt(item.style.top) || 0;
				const itemHeight = item.offsetHeight || this.estimateItemHeight(item);

				// Find which column this item is in
				const columnIndex = Math.round(itemLeft / (this.masonry.itemWidth + this.masonry.gap));

				// Ensure columnIndex is within bounds
				if (columnIndex >= 0 && columnIndex < this.masonry.columnCount) {
					// Update the column height to include this item
					const itemBottom = itemTop + itemHeight + this.masonry.gap;
					if (itemBottom > visibleColumnHeights[columnIndex]) {
						visibleColumnHeights[columnIndex] = itemBottom;
					}
				}
			}
		}

		// Find the shortest column from visible items
		let shortestColumnIndex = 0;
		let shortestHeight = visibleColumnHeights[0];

		for (let i = 1; i < visibleColumnHeights.length; i++) {
			if (visibleColumnHeights[i] < shortestHeight) {
				shortestHeight = visibleColumnHeights[i];
				shortestColumnIndex = i;
			}
		}

		// Position the button where the next item would go (exactly like positioning a new item)
		const nextItemLeft = shortestColumnIndex * (this.masonry.itemWidth + this.masonry.gap);
		const nextItemTop = visibleColumnHeights[shortestColumnIndex];

		// Position the button at the next item location
		this.loadMoreBtn.style.position = 'absolute';
		this.loadMoreBtn.style.left = `${nextItemLeft}px`;
		this.loadMoreBtn.style.top = `${nextItemTop}px`;
		this.loadMoreBtn.style.width = `${this.masonry.itemWidth}px`;
		this.loadMoreBtn.style.zIndex = '10';
	}

  resetLoadMoreButtonPosition() {
    if (!this.loadMoreBtn || !this.loadMoreBtnSpan) return;

		// Reset button to normal positioning for "Show Less" state
		this.loadMoreBtn.style.position = 'relative';
		this.loadMoreBtn.style.left = 'auto';
		this.loadMoreBtn.style.top = 'auto';
		this.loadMoreBtn.style.width = 'auto';
		this.loadMoreBtn.style.minHeight = 'auto';
		this.loadMoreBtn.style.backgroundColor = '';
		this.loadMoreBtn.style.border = '';
		this.loadMoreBtn.style.borderRadius = '';
		this.loadMoreBtn.style.opacity = '1'; // Always ensure button is visible
		this.loadMoreBtn.style.transform = 'none';
	}

	appendToMasonry(items) {
		// Add new items to masonry layout
		items.forEach(item => {
			this.positionMasonryItem(item);
		});
	}

	appendItemsToMasonry(items) {
		if (items.length === 0) return;

		// Position new items with optimized timing for iOS
		items.forEach((item, index) => {
			if (index === 0) {
				// Position first item immediately
				this.positionMasonryItemSync(item);
			} else {
				// Reduced delay for iOS optimization
				setTimeout(() => {
					this.positionMasonryItemSync(item);
				}, index * 5); // Reduced from 20ms to 5ms
			}
		});
	}

	handleResize() {
		// Debounce resize events - only recalculate settings, don't auto-relayout
		clearTimeout(this.resizeTimeout);
		this.resizeTimeout = setTimeout(() => {
			this.calculateMasonrySettings();
			// Remove automatic relayout - only update on button clicks
			
			// Update video blocks array
			this.updateVideoBlocks();

			// Reapply video handling and other features
			this.setupVideoHandling();
			this.setupTextTruncation();
		}, 250);
	}

	setupInitialState() {
		// Set all blocks to opacity 0 initially and hide blocks beyond initial load
		this.blocks.forEach((block, index) => {
			block.style.opacity = '0';
			block.style.transition = 'all ease 0.3s';

			if (index >= this.initialLoad) {
				block.setAttribute('data-hidden', 'true');
				block.style.display = 'none';
			}
		});

		this.currentlyVisible = Math.min(this.initialLoad, this.blocks.length);

		// Hide load more button if not needed
		if (this.blocks.length <= this.initialLoad) {
			if (this.loadMoreBtn) {
				this.loadMoreBtn.style.display = 'none';
			}
		} else {
			// Ensure the class is removed initially since we're not showing all items
			if (this.loadMoreBtn) {
				this.loadMoreBtn.classList.remove('all-items-visible');
			}
		}

		// Setup video blocks array for navigation
		this.updateVideoBlocks();
	}

	updateVideoBlocks() {
		// Get all visible video blocks
		this.videoBlocks = Array.from(this.querySelectorAll('.review-card--video:not([data-hidden="true"])'));
	}

	setupEventListeners() {
		if (this.loadMoreBtn) {
			this.loadMoreBtn.addEventListener('click', this.handleLoadMore.bind(this));
		}

		// Handle video click events - all videos open in modal
		this.blocks.forEach(block => {
			const video = block.querySelector('.review-card__video');
			if (video) {
				video.addEventListener('click', this.openVideoModal.bind(this));
				video.style.cursor = 'pointer';

				// Ensure autoplay videos are muted
				if (this.autoplayEnabled) {
					video.muted = true;
				}
			}
		});
	}

	setupVideoHandling() {
		const videos = this.querySelectorAll('.review-card__video:not([data-video-initialized])');

		videos.forEach(video => {
			// Mark as initialized to avoid duplicate setup
			video.setAttribute('data-video-initialized', 'true');

			// Set autoplay attribute based on section setting - always muted
			if (this.autoplayEnabled) {
				video.setAttribute('autoplay', '');
				video.setAttribute('muted', '');
				video.setAttribute('loop', '');
				video.muted = true; // Ensure muted
			} else {
				video.removeAttribute('autoplay');
				video.muted = true; // Still muted even without autoplay
			}

			// Handle video loading errors
			video.addEventListener('error', (e) => {
				console.warn('Video failed to load:', video.src, e);
				const sources = video.querySelectorAll('source');
				console.warn('Video sources:', Array.from(sources).map(s => s.src));
				const container = video.closest('.review-card--video');
				if (container) {
					container.style.backgroundColor = '#f5f5f5';
					container.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; padding: 20px; text-align: center;"><div><p>Video unavailable</p><small style="color: #999;">Please check video upload or URL</small></div></div>';
				}
			});

			// Handle video load success
			video.addEventListener('loadeddata', () => {
				video.style.opacity = '1';
			});

			// Set initial opacity and transition for videos
			video.style.opacity = '1'; // Videos should be visible by default
			video.style.transition = 'all ease 0.3s';
		});
	}

	setupIntersectionObserver() {
		// Lazy load videos when they come into view
		if ('IntersectionObserver' in window) {
			const videoObserver = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const video = entry.target;
						if (video.dataset.src && !video.src) {
							video.src = video.dataset.src;
							video.load();
						}

						// Auto-play video if enabled and in view (always muted)
						if (this.autoplayEnabled && video.paused) {
							video.muted = true; // Ensure muted
							video.play().catch(e => {
								console.warn('Video autoplay failed:', e);
							});
						}
					} else {
						// Pause video when out of view to save bandwidth
						const video = entry.target;
						if (this.autoplayEnabled && !video.paused) {
							video.pause();
						}
					}
				});
			}, {
				rootMargin: '50px',
				threshold: 0.1
			});

			// Observe all videos
			this.querySelectorAll('.review-card__video').forEach(video => {
				videoObserver.observe(video);
			});
		}
	}

	handleLoadMore() {
		if (this.isExpanded) {
			// Show less - collapse back to initial state
			this.collapseBlocks();
		} else {
			// Load more blocks
			this.expandBlocks();
		}
	}

	expandBlocks() {
		const nextBatch = Math.min(
			this.currentlyVisible + this.loadIncrement,
			this.blocks.length
		);

		// Hide the load more button temporarily during expansion
		if (this.loadMoreBtn) {
			this.loadMoreBtn.style.opacity = '0';
		}

		// Get newly visible blocks
		const newlyVisibleBlocks = [];
		for (let i = this.currentlyVisible; i < nextBatch; i++) {
			const block = this.blocks[i];
			if (block) {
				block.removeAttribute('data-hidden');
				block.style.display = '';
				block.style.opacity = '1'; // Show immediately
				block.style.transition = 'all ease 0.3s';

				newlyVisibleBlocks.push(block);
			}
		}

		this.currentlyVisible = nextBatch;

		// Update button state
		if (this.currentlyVisible >= this.blocks.length) {
			// All blocks are now visible
			this.isExpanded = true;
			if (this.loadMoreBtn) {
        this.loadMoreBtnSpan.textContent = this.showLessText;
				this.loadMoreBtn.classList.add('all-items-visible');
				this.loadMoreBtn.style.opacity = '1'; // Ensure button is visible
			}
		} else {
			// Still have more items to show
			if (this.loadMoreBtn) {
				this.loadMoreBtn.classList.remove('all-items-visible');
			}
		}

		// Only position items when button is clicked - no automatic repositioning
		const sortedNewItems = this.sortItemsForMobile(newlyVisibleBlocks);
		this.appendItemsToMasonry(sortedNewItems);

		// Setup videos for newly visible blocks
		setTimeout(() => {
			// Apply text truncation to newly visible items
			this.setupTextTruncation();

			// Update video blocks array
			this.updateVideoBlocks();

			// Trigger video setup for newly visible videos
			this.setupVideoHandling();

			// Ensure videos in new blocks are visible
			newlyVisibleBlocks.forEach(block => {
				const video = block.querySelector('.review-card__video');
				if (video) {
					video.style.opacity = '1';
				}
			});

			// Show the load more button (if not all items are visible)
			if (this.loadMoreBtn && this.currentlyVisible < this.blocks.length) {
				this.loadMoreBtn.style.opacity = '1';
			}
		}, 50); // Reduced timeout
	}

	collapseBlocks() {
		// Calculate how many items to hide (reduce by the same increment)
		const newVisibleCount = Math.max(this.initialLoad, this.currentlyVisible - this.loadIncrement);

		// Hide blocks beyond the new visible count
		for (let i = newVisibleCount; i < this.blocks.length; i++) {
			const block = this.blocks[i];
			if (block) {
				block.setAttribute('data-hidden', 'true');
				block.style.display = 'none';
				block.style.opacity = '0';
			}
		}

		this.currentlyVisible = newVisibleCount;

		// Check if we're back to initial state or if we can show less
		if (this.currentlyVisible <= this.initialLoad) {
			this.isExpanded = false;
			if (this.loadMoreBtn) {
        this.loadMoreBtnSpan.textContent = this.loadMoreText;
				this.loadMoreBtn.classList.remove('all-items-visible');
				// this.resetLoadMoreButtonPosition();
			}
		} else {
			// Still have more than initial, keep "Show Less" but update position
			if (this.loadMoreBtn) {
        this.loadMoreBtnSpan.textContent = this.showLessText;
				// Check if we're showing all items
				if (this.currentlyVisible >= this.blocks.length) {
					this.loadMoreBtn.classList.add('all-items-visible');
					this.loadMoreBtn.style.opacity = '1'; // Ensure button is visible
				} else {
					this.loadMoreBtn.classList.remove('all-items-visible');
					this.loadMoreBtn.style.opacity = '1'; // Ensure button is visible
				}
			}
		}

		// Only relayout when button is clicked - no automatic repositioning during touch/swipe
		setTimeout(() => {
			this.relayoutMasonry();
		}, 50);

		// Scroll back to section top only if we're back to initial state
		if (this.currentlyVisible <= this.initialLoad) {
			this.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	resetMasonryToInitial() {
		// Reset column heights
		this.masonry.columnHeights = new Array(this.masonry.columnCount).fill(0);

		// Get only the initially visible items
		const initialItems = this.blocks.slice(0, this.initialLoad);

		// Sort items for mobile alternating pattern if needed
		const sortedItems = this.sortItemsForMobile(initialItems);

		// Reposition only the initial items
		this.positionItemsSequentially(sortedItems);
	}

	adjustContainerHeight() {
		// For fixed height layout, we don't need to adjust height dynamically
		// The height is fixed at 702px for desktop and adjusted via CSS for responsive
		// This method is kept for compatibility but doesn't modify height

		// Optional: Could add logic here to detect if content overflows
		// and show a scroll indicator or adjust layout accordingly
	}

	setupTextTruncation() {
		// Text truncation has been removed - all text will display in full
		// This method is kept for compatibility but no longer truncates text
	}

	setupVideoModal() {
		if (!this.videoModal) return;

		// Close modal event listeners
		this.modalClose?.addEventListener('click', this.closeVideoModal.bind(this));

		// Navigation event listeners
		this.modalPrev?.addEventListener('click', this.navigateToPrevVideo.bind(this));
		this.modalNext?.addEventListener('click', this.navigateToNextVideo.bind(this));

		// Keyboard event listener for ESC key and arrow keys
		document.addEventListener('keydown', (e) => {
			if (this.videoModal.style.display === 'flex') {
				switch (e.key) {
					case 'Escape':
						this.closeVideoModal();
						break;
					case 'ArrowLeft':
						this.navigateToPrevVideo();
						break;
					case 'ArrowRight':
						this.navigateToNextVideo();
						break;
				}
			}
		});
	}


	openVideoModal(event) {
		event.preventDefault();
		event.stopPropagation();

		const clickedVideo = event.target;
		const videoCard = clickedVideo.closest('.review-card--video');

		if (!this.videoModal || !this.modalVideoContainer || !videoCard) return;

		// Update video blocks and find current index
		this.updateVideoBlocks();
		this.currentVideoIndex = this.videoBlocks.indexOf(videoCard);

		// Show the modal
		this.videoModal.style.display = 'flex';
		document.body.classList.add('birdie-stop_scrolling');

		// Load the video for this index
		this.loadVideoAtIndex(this.currentVideoIndex);
	}

	loadVideoAtIndex(index) {
		if (index < 0 || index >= this.videoBlocks.length) return;

		const videoCard = this.videoBlocks[index];
		if (!videoCard) return;

		// Check if there's a long video version
		const longVideoSources = videoCard.dataset.longVideoSources;
		let videoSourcesToUse = null;

		if (longVideoSources) {
			try {
				videoSourcesToUse = JSON.parse(longVideoSources);
			} catch (e) {
				console.warn('Failed to parse long video sources:', e);
			}
		}

		// Create video element
		const modalVideo = document.createElement('video');
		modalVideo.controls = true;
		modalVideo.muted = false; // Enable sound in modal
		modalVideo.style.width = '380px';
		modalVideo.style.height = '600px';
		modalVideo.style.objectFit = 'cover';
		modalVideo.style.objectPosition = 'center';
		modalVideo.style.borderRadius = '20px';
		modalVideo.setAttribute('playsinline', '');

		// Add video sources
		if (videoSourcesToUse && videoSourcesToUse.length > 0) {
			// Use long video sources
			videoSourcesToUse.forEach(source => {
				const sourceElement = document.createElement('source');
				sourceElement.src = source.url;
				sourceElement.type = source.type;
				modalVideo.appendChild(sourceElement);
			});
		} else {
			// Fallback to short video sources
			const shortVideo = videoCard.querySelector('.review-card__video');
			if (shortVideo) {
				const shortSources = shortVideo.querySelectorAll('source');
				shortSources.forEach(source => {
					const sourceElement = document.createElement('source');
					sourceElement.src = source.src;
					sourceElement.type = source.type;
					modalVideo.appendChild(sourceElement);
				});
			}
		}

		// Clear previous video and add new one
		this.modalVideoContainer.innerHTML = '';
		this.modalVideoContainer.appendChild(modalVideo);

		// Load the video (don't autoplay in modal)
		modalVideo.load();

		// Add error handling
		modalVideo.addEventListener('error', (e) => {
			console.error('Modal video error:', e);
		});

		// Log successful load
		modalVideo.addEventListener('loadeddata', () => {
			// console.log('Modal video loaded successfully');
		});
	}

	navigateToPrevVideo() {
		if (this.videoBlocks.length === 0) return;

		this.currentVideoIndex = this.currentVideoIndex === 0
			? this.videoBlocks.length - 1
			: this.currentVideoIndex - 1;

		this.loadVideoAtIndex(this.currentVideoIndex);
	}

	navigateToNextVideo() {
		if (this.videoBlocks.length === 0) return;

		this.currentVideoIndex = this.currentVideoIndex === this.videoBlocks.length - 1
			? 0
			: this.currentVideoIndex + 1;

		this.loadVideoAtIndex(this.currentVideoIndex);
	}

	closeVideoModal() {
		if (!this.videoModal) return;

		// Find and pause the video element
		const modalVideo = this.modalVideoContainer?.querySelector('.review-card__video');
		if (modalVideo) {
			modalVideo.pause();
			modalVideo.currentTime = 0;
		}

		// Clear the modal content
		this.modalVideoContainer.innerHTML = '';

		// Hide the modal
		this.videoModal.style.display = 'none';
		document.body.classList.remove('birdie-stop_scrolling');
	}

	// Public method to refresh the component (useful for dynamic content)
	refresh() {
		this.blocks = Array.from(this.querySelectorAll('.review-card'));
		this.setupInitialState();
		this.setupVideoHandling();
	}

	// Public method to update settings
	updateSettings(settings) {
		if (settings.autoplay !== undefined) {
			this.autoplayEnabled = settings.autoplay;
			this.setupVideoHandling();
		}

		if (settings.initialLoad !== undefined) {
			this.initialLoad = settings.initialLoad;
			this.setupInitialState();
		}

		if (settings.loadIncrement !== undefined) {
			this.loadIncrement = settings.loadIncrement;
		}
	}
}

// Register the custom element
customElements.define('reviews-with-videos-container', ReviewsWithVideos);

// Fallback for browsers that don't support custom elements
if (!window.customElements) {
	document.addEventListener('DOMContentLoaded', () => {
		const containers = document.querySelectorAll('[data-autoplay]');
		containers.forEach(container => {
			new ReviewsWithVideos.call(container);
		});
	});
}

// Export for potential external use
window.ReviewsWithVideos = ReviewsWithVideos;
